import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Create } from "@refinedev/antd";
import { Form, Input, InputNumber, notification } from "antd";
import { apiUrl } from "@/consts";

// Our default membership commission (Stripe Connect application_fee_percent).
// Kept in sync with SEASON_APPLICATION_FEE_PERCENT on the backend; pre-filled
// here so new clubs are created with an explicit commission rather than relying
// on the code fallback. Admins can override per club.
const DEFAULT_SEASON_FEE_PERCENT = 3;

type CommissionBody = {
  data: { commissionPercent: number; defaultPercent: number; isOverridden: boolean };
};

type CreateClubResponse = {
  message: string;
  club: { id: string; name: string; slug: string };
  invite: { email: string; expiresAt: string };
};

export function ClubsCreate() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { mutate } = useCustomMutation<CreateClubResponse>();

  // Current platform lotto commission, pre-filled below so new clubs are created
  // with an explicit rate — same treatment as the membership commission above,
  // except the value is read from the API rather than hardcoded.
  const { data: commissionData } = useCustom<CommissionBody>({
    url: `${apiUrl}/lotto-commission`,
    method: "get",
  });
  const globalLottoPercent = commissionData?.data?.data?.commissionPercent ?? null;

  // Seed the field once the rate arrives. It can't go in `initialValues` (the
  // fetch resolves after mount), and we skip seeding if the admin already typed
  // something so a slow response can't clobber their input.
  useEffect(() => {
    if (globalLottoPercent == null) return;
    if (form.isFieldTouched("lottoCommissionPercent")) return;
    if (form.getFieldValue("lottoCommissionPercent") != null) return;
    form.setFieldValue("lottoCommissionPercent", globalLottoPercent);
  }, [globalLottoPercent, form]);

  function handleFinish(values: any) {
    setSaving(true);
    mutate(
      {
        url: `${apiUrl}/clubs`,
        method: "post",
        values: {
          name: values.name,
          ownerEmail: values.ownerEmail,
          ownerName: values.ownerName,
          contactPerson: values.contactPerson || undefined,
          contactEmail: values.contactEmail || undefined,
          contactPhone: values.contactPhone || undefined,
          description: values.description || undefined,
          seasonFeePercent:
            values.seasonFeePercent === undefined || values.seasonFeePercent === null
              ? null
              : Number(values.seasonFeePercent),
          lottoCommissionPercent:
            values.lottoCommissionPercent === undefined ||
            values.lottoCommissionPercent === null
              ? null
              : Number(values.lottoCommissionPercent),
          message: values.message || undefined,
        },
      },
      {
        onSuccess: (resp) => {
          setSaving(false);
          const club = resp?.data?.club;
          notification.success({
            message: "Club created",
            description: resp?.data?.message ?? "Invite sent to the owner.",
          });
          if (club?.id) {
            navigate(`/clubs/${club.id}`);
          } else {
            navigate("/clubs");
          }
        },
        onError: (err: any) => {
          setSaving(false);
          notification.error({
            message: "Failed to create club",
            description:
              err?.response?.data?.error ?? err?.message ?? "An unexpected error occurred.",
          });
        },
      }
    );
  }

  return (
    <Create
      saveButtonProps={{ loading: saving, onClick: () => form.submit() }}
      title="Create club"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ seasonFeePercent: DEFAULT_SEASON_FEE_PERCENT }}
      >
        <Form.Item
          label="Club name"
          name="name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="FC Example" />
        </Form.Item>

        <Form.Item
          label="Owner email"
          name="ownerEmail"
          rules={[
            { required: true, message: "Owner email is required" },
            { type: "email", message: "Please enter a valid email address" },
          ]}
        >
          <Input placeholder="owner@club.com" />
        </Form.Item>

        <Form.Item
          label="Owner name"
          name="ownerName"
          rules={[{ required: true, message: "Owner name is required" }]}
        >
          <Input placeholder="Jane Doe" />
        </Form.Item>

        <Form.Item label="Contact person" name="contactPerson">
          <Input />
        </Form.Item>

        <Form.Item
          label="Contact email"
          name="contactEmail"
          rules={[{ type: "email", message: "Please enter a valid email address" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Contact phone" name="contactPhone">
          <Input />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item
          label="Membership commission %"
          name="seasonFeePercent"
          extra="Our membership commission on each season invoice. Defaults to 3%; adjust or clear to use the platform default."
        >
          <InputNumber min={0} max={100} step={0.01} addonAfter="%" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label="Lotto commission %"
          name="lottoCommissionPercent"
          extra={
            globalLottoPercent != null
              ? `Our commission on this club's lotto sales. Defaults to ${globalLottoPercent}%; adjust or clear to use the platform default.`
              : "Our commission on this club's lotto sales. Adjust or clear to use the platform default."
          }
        >
          <InputNumber min={0} max={100} step={0.01} addonAfter="%" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Personal note in invite email" name="message">
          <Input.TextArea rows={3} placeholder="Optional message included in the invite email" />
        </Form.Item>
      </Form>
    </Create>
  );
}
